import { Column } from '@tanstack/react-table';
import { Dropdown } from '@carbon/react';
import { useMemo } from 'react';
import { DataTableColumnMeta } from './types';

// ─────────────────────────────────────
// Types
// ─────────────────────────────────────

export interface DropdownItem {
  value: string;
  label: string;
}

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface HeaderSelectFilterProps<TData, TValue> {
  column: Column<TData, TValue>;
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Inline single-select filter for the table header filter row.
 * Displays a compact dropdown using Carbon Dropdown.
 * Options are derived from column meta.filterOptions.
 * @param {HeaderSelectFilterProps} props - Component props
 * @param {Column} props.column - TanStack Table column instance
 * @return {JSX.Element} The header select filter element
 */
export function HeaderSelectFilter<TData, TValue>({
  column,
}: HeaderSelectFilterProps<TData, TValue>) {
  const meta = column.columnDef.meta as DataTableColumnMeta | undefined;
  const filterValue = (column.getFilterValue() as string) ?? '';

  const options = useMemo(
    () => meta?.filterOptions ?? [],
    [meta?.filterOptions]
  );

  const selectedItem = options.find((opt) => opt.value === filterValue) ?? null;

  return (
    <Dropdown
      id={`filter-${column.id}`}
      titleText=""
      label="Filter..."
      size="sm"
      items={options}
      itemToString={(item: DropdownItem | null) => item?.label ?? ''}
      selectedItem={selectedItem}
      onChange={({
        selectedItem: item,
      }: {
        selectedItem: DropdownItem | null;
      }) => {
        column.setFilterValue(item?.value || undefined);
      }}
    />
  );
}
